import { useEffect, useMemo, useState } from "react";
import GameShell from "../GameShell";

/**
 * Builder's Workshop — WISC blocks inspired.
 * Show a 3x3 target pattern. The child taps each empty cell to cycle through colors
 * until the grid matches the target.
 * 3 rounds, increasing difficulty (3x3 only for MVP).
 */
export default function BuildersWorkshop() {
  return (
    <GameShell gameId="builders_workshop" title="Oficina do Construtor">
      {({ onFinish }) => <BlocksGame onFinish={onFinish} />}
    </GameShell>
  );
}

const PALETTE = ["#F8FAFC", "#38BDF8", "#FBBF24", "#F472B6", "#A3E635"];
const N = 3;
const ROUNDS = 3;

function randomTarget() {
  return Array.from({ length: N * N }, () => 1 + Math.floor(Math.random() * (PALETTE.length - 1)));
}

function BlocksGame({ onFinish }) {
  const [round, setRound] = useState(0);
  const [target, setTarget] = useState(() => randomTarget());
  const [grid, setGrid] = useState(() => Array(N * N).fill(0));
  const [errors, setErrors] = useState(0);
  const [taps, setTaps] = useState(0);
  const [roundTimes, setRoundTimes] = useState([]);
  const [startedAt] = useState(() => performance.now());
  const [roundStart, setRoundStart] = useState(() => performance.now());

  const solved = useMemo(
    () => grid.every((v, i) => v === target[i]),
    [grid, target]
  );

  useEffect(() => {
    if (!solved) return;
    const rt = performance.now() - roundStart;
    const newTimes = [...roundTimes, rt];
    setRoundTimes(newTimes);
    if (round + 1 >= ROUNDS) {
      const elapsed = performance.now() - startedAt;
      const score = Math.max(0, Math.round(ROUNDS * 30 - errors * 5));
      onFinish({
        score,
        duration_ms: Math.round(elapsed),
        metrics: {
          rounds: ROUNDS,
          errors,
          taps,
          avg_round_ms: Math.round(newTimes.reduce((a, b) => a + b, 0) / newTimes.length),
        },
      });
      return;
    }
    // next round
    const t = setTimeout(() => {
      setRound((r) => r + 1);
      setTarget(randomTarget());
      setGrid(Array(N * N).fill(0));
      setRoundStart(performance.now());
    }, 800);
    return () => clearTimeout(t);
  }, [solved]); // eslint-disable-line

  const tap = (i) => {
    setTaps((t) => t + 1);
    setGrid((g) => {
      const next = [...g];
      const cur = next[i];
      const newVal = (cur + 1) % PALETTE.length;
      next[i] = newVal;
      if (newVal !== target[i] && cur === target[i]) {
        // they had it right and changed it -> count as error
        setErrors((e) => e + 1);
      }
      return next;
    });
  };

  return (
    <div className="mt-4 max-w-3xl mx-auto" data-testid="builders-area">
      <div className="text-center text-white drop-shadow mb-4">
        <div className="text-sm">
          Replique o modelo da esquerda na grade da direita. Toque na peça para mudar a cor.
        </div>
        <div className="text-xs mt-1 opacity-80">Rodada {round + 1} / {ROUNDS}</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Grid title="Modelo" cells={target} interactive={false} />
        <Grid title="Sua construção" cells={grid} interactive onTap={tap} testIdPrefix="blocks-cell" />
      </div>
      {solved && (
        <div className="text-center mt-4 text-3xl pop-in">🎉</div>
      )}
    </div>
  );
}

function Grid({ title, cells, interactive, onTap, testIdPrefix }) {
  return (
    <div className="kid-card">
      <div className="text-center font-bold text-slate-700 mb-2">{title}</div>
      <div className="grid grid-cols-3 gap-2">
        {cells.map((v, i) => (
          <button
            key={i}
            disabled={!interactive}
            onClick={interactive ? () => onTap(i) : undefined}
            className="aspect-square rounded-xl border-2 border-slate-200 transition active:scale-95"
            style={{ background: PALETTE[v] }}
            data-testid={testIdPrefix ? `${testIdPrefix}-${i}` : undefined}
          />
        ))}
      </div>
    </div>
  );
}
